import { supabase } from '@/integrations/supabase/client';

export interface RagTriggerResult {
  success: boolean;
  region: string;
  draftId?: string;
  message?: string;
  error?: string;
}

/**
 * Trigger RAG article generation for a specific region
 */
export async function triggerRagGeneration(region: string): Promise<RagTriggerResult> {
  try {
    const { data, error } = await supabase.functions.invoke('generate-rag-article', {
      body: {
        region,
        skipScheduleValidation: true, // Allow manual testing outside schedule
        dryRun: false
      }
    });

    if (error) {
      throw error;
    }

    if (!data || !data.success) {
      return {
        success: false,
        region,
        error: data?.message || 'Unknown error occurred'
      };
    }

    return {
      success: true,
      region,
      draftId: data.draftId,
      message: data.message
    };
  } catch (error) {
    console.error(`[ragTriggerService] Error generating article for ${region}:`, error);
    return {
      success: false,
      region,
      error: error instanceof Error ? error.message : 'Failed to generate article'
    };
  }
}

/**
 * Trigger RAG article generation for all regions sequentially
 */
export async function triggerAllRagGenerations(): Promise<RagTriggerResult[]> {
  const regions = [
    'europa',
    'nord_amerika',
    'asia',
    'latin_amerika',
    'midtosten',
    'afrika',
    'skandinavia'
  ];

  const results: RagTriggerResult[] = [];

  for (const region of regions) {
    const result = await triggerRagGeneration(region);
    results.push(result);
    
    // Add a small delay between requests to avoid overwhelming the system
    if (region !== regions[regions.length - 1]) {
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  }

  return results;
}
